// STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · testmode 전용 리모컨 상품 조작 레이어 (기본 OFF · 학습한 신호만 실행).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, Gamepad2 } from 'lucide-react';
import useRemoteGuard from '@/hooks/useRemoteGuard';
import { pickRelative } from '@/lib/liveRelative';
import { BUTTONS, BTN_LABEL, MENU_START, classify, cursorId, decide, decideMenu, sameSignal, shutterConflict, sigFromGesture, validateMap } from '@/lib/remoteControl';
import useNoticeLibrary from '@/hooks/useNoticeLibrary';
import { RemoteMenuPanel, menuRows, ROOT_LABEL } from '@/components/seller/live/RemoteMenuPanel';

const SHUTTER_KEY = 'livemoa.remoteShutter.v1';
const SHUTTER_COOLDOWN_MS = 3000;
const RECOVER_WINDOW_MS = 120000;
const loadShutter = () => { try { return JSON.parse(localStorage.getItem(SHUTTER_KEY)) || null; } catch { return null; } };

const STORE_KEY = 'livemoa.remoteMap.v1';
const COLLAPSE_KEY = 'livemoa.remoteBarCollapsed.v1';
const DEDUPE_MS = 400;
const loadMap = () => { try { return JSON.parse(localStorage.getItem(STORE_KEY)) || null; } catch { return null; } };

const Chip = ({ children, onClick, testId, tone }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className={`min-h-[32px] px-3 rounded-full text-[11px] font-black whitespace-nowrap shrink-0 transition-colors ${tone === 'red' ? 'bg-[#FF3B30] text-white' : 'bg-white/15 text-white hover:bg-white/25'}`}>
    {children}
  </button>
);

const LearnPrompt = ({ step, onCancel }) => (
  <div className="fixed inset-x-4 top-32 z-[75] rounded-2xl bg-black/85 backdrop-blur-md p-4 text-white text-center" data-testid="remote-ctl-learn">
    <p className="text-[11px] text-white/60">{step === 'shutter' ? '셔터 학습 · 음량이 바뀔 수 있어요' : `버튼 학습 ${step + 1}/${BUTTONS.length}`} · 학습 중에는 상품 조작 없음</p>
    <p className="mt-1 text-lg font-black" data-testid="remote-ctl-learn-target">리모컨 '{step === 'shutter' ? '하단 셔터' : BTN_LABEL[BUTTONS[step]]}' 버튼을 1번 누르세요</p>
    <div className="mt-3"><Chip testId="remote-ctl-learn-cancel" onClick={onCancel}>학습 취소</Chip></div>
  </div>
);

export const RemoteControlLayer = ({ onExit, items, activePid, listOpen, openList, closeList, introducePid, setCursorPid, canIntroduce, blocked, onShutter = null, ctx = null, ops = null, past = null, onQuickAdd = null, touchBlock = true }) => {
  const [map, setMap] = useState(loadMap);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; } });
  const toggleCollapsed = (c) => { setCollapsed(c); try { localStorage.setItem(COLLAPSE_KEY, c ? '1' : '0'); } catch { /* 메모리만 */ } };
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);
  menuRef.current = menu;
  const menuReturn = useRef(null);
  const hasMenu = !!ops;
  const { items: library } = useNoticeLibrary();
  const [shutter, setShutter] = useState(loadShutter);
  const lastShutter = useRef(-1e9);
  const [learn, setLearn] = useState(null);
  const [draft, setDraft] = useState({});
  const [feedback, setFeedback] = useState(null);
  const nav = useRef({ cursor: null, moved: false });
  const last = useRef({ b: null, t: 0 });
  const timer = useRef(null);
  // QUICKADD-TOUCH-RECOVERY-1 · 셔터 직후 첫 5버튼 입력은 터치 복구용(실제 입력은 그대로 도착 · Studio 명령만 생략).
  const recoverPending = useRef(0);
  useEffect(() => {
    let penAt = -1e9;
    const pen = (e) => { if (e.pointerType === 'pen' || e.pointerType === 'mouse') penAt = performance.now(); };
    // 손가락 터치가 실제로 동작하면(펜 신호와 무관한 touchend) 복구 대기 해제.
    const clear = () => { if (performance.now() - penAt > 500) recoverPending.current = 0; };
    window.addEventListener('pointerdown', pen, { capture: true, passive: true });
    window.addEventListener('pointerup', pen, { capture: true, passive: true });
    window.addEventListener('touchend', clear, { capture: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', pen, { capture: true });
      window.removeEventListener('pointerup', pen, { capture: true });
      window.removeEventListener('touchend', clear, { capture: true });
    };
  }, []);
  const v = validateMap(map);

  const flash = useCallback((text, ok = true) => {
    setFeedback({ text, ok });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFeedback(null), 1400);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  // 목록이 닫히면 이동 상태 초기화 · 다시 열리면(손가락 포함) 소개 중 상품을 선택 위치로 표시·복원.
  useEffect(() => {
    if (!listOpen) { nav.current = { cursor: null, moved: false }; setCursorPid(null); return; }
    if (nav.current.cursor == null) {
      const idx = items.findIndex((p) => p.product_id === activePid);
      setCursorPid(items[idx >= 0 ? idx : 0]?.product_id || null);
    }
  }, [listOpen, setCursorPid]); // eslint-disable-line react-hooks/exhaustive-deps

  const relative = (delta) => {
    if (!canIntroduce) { flash('테스트 LIVE 시작 후 소개할 수 있어요', false); return; }
    const pid = pickRelative(items, activePid, delta);
    if (!pid) { flash(delta > 0 ? '다음 판매 가능한 상품이 없어요' : '이전 판매 가능한 상품이 없어요', false); return; }
    introducePid(pid);
    flash(delta > 0 ? '다음 상품 소개' : '이전 상품 소개');
  };
  const pick = (index) => {
    const p = items[index];
    if (!p) return;
    if (!canIntroduce) { flash('테스트 LIVE 시작 후 소개할 수 있어요', false); return; }
    introducePid(p.product_id);
    flash(`선택 확정 · ${p.name}`);
  };

  // TIMER-NOTICE-CONTROL-1 · 메뉴 모드 — 상품 소개 명령과 완전 분리.
  // QUICK-MENU-SLIDE-1 · 지난 상품 슬라이드 (기존 Studio 슬라이더 state REUSE · 소개 상품 변경 없음).
  const runPast = (r) => {
    if (!past) { flash('지난 상품 슬라이드를 사용할 수 없어요', false); return; }
    const n = past.list.length;
    if (r.kind === 'toggle') {
      if (past.mode === 'off') { past.on(); flash('지난 상품 슬라이드 ON'); } else { past.off(); flash('지난 상품 슬라이드 OFF'); }
      return;
    }
    if (past.mode === 'off') { flash('슬라이드를 먼저 켜주세요', false); return; }
    if (!n) { flash('아직 소개한 지난 상품이 없어요', false); return; }
    if (r.kind === 'play') {
      if (past.playing) { past.stop(); flash('자동 재생 정지'); } else { past.play(); flash('자동 재생 시작'); }
      return;
    }
    const i = r.kind === 'pick' ? r.i : (past.index + (r.kind === 'next' ? 1 : -1) + n) % n;
    past.show(i);
    flash(`지난 상품 ${i + 1}/${n} 표시`);
  };
  const runRow = (level, r) => {
    if (level === 'past') { runPast(r); return; }
    if (level === 'timer') {
      if (r.kind === 'preset') {
        if (!canIntroduce) { flash('테스트 LIVE 중에 시작할 수 있어요', false); return; }
        ops.timer('start', { duration_seconds: Math.round(r.p.minutes * 60), label: r.p.label });
        flash(`타이머 시작 · ${r.p.label}`);
        return;
      }
      const st = ctx?.timer?.status;
      if (r.kind === 'pause') {
        if (st === 'running') { ops.timer('pause'); flash('타이머 일시정지'); return; }
        if (st === 'paused') { ops.timer('resume'); flash('타이머 재개'); return; }
        flash('실행 중인 타이머가 없어요', false); return;
      }
      if (r.kind === 'stop') { ops.timer('stop'); flash('타이머 정지'); }
      return;
    }
    if (level === 'notice') {
      if (r.kind === 'show') { ops.notice(r.n); flash('공지 표시'); } else { ops.notice(null); flash('공지 내리기'); }
      return;
    }
    if (level === 'ext') {
      ops.ext(r.kind === 'sel' ? r.n.id : null);
      flash(r.kind === 'sel' ? '외부공지 선택' : '외부공지 선택 해제');
    }
  };
  const runMenu = (button) => {
    const m = menuRef.current;
    const rows = m.level === 'root' ? [] : menuRows(m.level, { ctx, library, past });
    const { state, action } = decideMenu(m, button, { len: rows.length });
    if (!action) return;
    if (action.type === 'leave' || action.type === 'quick') {
      setMenu(null);
      menuReturn.current = null;
      nav.current = { cursor: null, moved: false };
      closeList();
      if (action.type === 'quick') {
        if (!onQuickAdd) { flash('빠른등록을 사용할 수 없어요', false); return; }
        onQuickAdd();
        flash('빠른등록 · 3초 후 캡처');
        return;
      }
      flash('방송 화면으로 복귀');
      return;
    }
    if (action.type === 'exit') {
      setMenu(null);
      const back = menuReturn.current;
      menuReturn.current = null;
      if (back) {
        nav.current = back;
        openList();
        setCursorPid(cursorId(items, back.cursor));
      }
      flash('상품 제어로 복귀');
      return;
    }
    setMenu(state);
    if (action.type === 'enter') flash(ROOT_LABEL[action.level]);
    if (action.type === 'run') runRow(m.level, rows[action.idx]);
  };

  const run = (button) => {
    if (menuRef.current) { runMenu(button); return; }
    const activeIdx = items.findIndex((p) => p.product_id === activePid);
    const { state, action } = decide(nav.current, button, { open: listOpen, count: items.length, activeIdx, menu: hasMenu });
    nav.current = state;
    if (!action) return;
    if (action.type === 'menu') {
      menuReturn.current = { cursor: state.cursor, moved: state.moved };
      closeList();
      setMenu({ level: 'root', idx: MENU_START });
      flash('통합 제어 메뉴');
      return;
    }
    if (action.type === 'open') { openList(); setCursorPid(items[state.cursor]?.product_id || null); flash('상품 목록 열기'); }
    if (action.type === 'relative') relative(action.delta);
    if (action.type === 'cursor') {
      setCursorPid(cursorId(items, action.index));
      flash(action.index < 0 || action.index >= items.length ? '목록 닫기 · 중앙을 누르면 소개 변경 없이 닫기' : `선택 위치 ${action.index + 1}/${items.length}`);
    }
    if (action.type === 'pick') { closeList(); pick(action.index); }
    if (action.type === 'close') { closeList(); flash(action.cancel ? '목록 닫기 · 소개 상품 그대로' : '상품 목록 닫기'); }
    if (action.type === 'close_relative') { closeList(); relative(action.delta); }
  };

  const onGesture = (g) => {
    const sig = sigFromGesture(g);
    if (learn === 'shutter') {
      setLearn(null);
      const hit = shutterConflict(map, sig);
      if (hit) { flash(`셔터 학습 실패 · '${BTN_LABEL[hit]}' 버튼과 구분 불가`, false); return; }
      setShutter(sig);
      try { localStorage.setItem(SHUTTER_KEY, JSON.stringify(sig)); } catch { /* 메모리만 */ }
      flash('셔터 학습 완료');
      return;
    }
    // STUDIO-BR-TX3-SHUTTER-CAPTURE-1 · 리모컨 ON 중 가상 터치가 캡처 버튼 위에 떨어지면 셔터로 처리 (5버튼 매핑 무변경).
    const onShutterBtn = learn == null && onShutter && g.cx != null
      && document.elementFromPoint(g.cx, g.cy)?.closest('[data-shutter-capture="1"]');
    if (onShutterBtn || (learn == null && onShutter && sameSignal(shutter, sig) && !shutterConflict(map, sig))) {
      const now = performance.now();
      if (now - lastShutter.current < SHUTTER_COOLDOWN_MS) return;
      lastShutter.current = now;
      if (blocked) { flash('다른 화면이 열려 있어 셔터 무시', false); return; }
      flash('셔터 · 상품 캡처');
      recoverPending.current = now;
      onShutter();
      return;
    }
    if (learn != null) {
      const next = { ...draft, [BUTTONS[learn]]: sig };
      if (learn + 1 < BUTTONS.length) { setDraft(next); setLearn(learn + 1); return; }
      const res = validateMap(next);
      setLearn(null); setDraft({});
      if (res.ok) { setMap(next); try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* 저장 실패 시 메모리만 */ } flash('학습 완료 · 리모컨 조작 준비됨'); } else flash(`학습 실패 · ${res.reason}`, false);
      return;
    }
    if (!v.ok) { flash('버튼 학습이 필요해요', false); return; }
    const { button, reason } = classify(map, sig);
    if (!button) { flash(reason === 'ambiguous' ? '모호한 신호 · 무시' : '알 수 없는 신호 · 무시', false); return; }
    const now = performance.now();
    if (last.current.b === button && now - last.current.t < DEDUPE_MS) return;
    last.current = { b: button, t: now };
    if (recoverPending.current && now - recoverPending.current < RECOVER_WINDOW_MS) {
      recoverPending.current = 0;
      flash('터치 복구 · 상품·메뉴 변경 없음');
      return;
    }
    recoverPending.current = 0;
    if (blocked) { flash('다른 화면이 열려 있어 무시', false); return; }
    run(button);
  };

  useRemoteGuard(true, onGesture, { touchBlock });

  return (
    <>
      {collapsed ? (
        <button type="button" onClick={() => toggleCollapsed(false)} data-testid="remote-ctl-expand" aria-label="리모컨 바 펼치기"
                className={`fixed top-16 right-[3.75rem] md:right-auto md:left-1/2 md:-translate-x-1/2 ${blocked ? 'z-[40]' : 'z-[70]'} flex items-center gap-1 rounded-full bg-black/75 backdrop-blur-md px-2.5 py-1.5 text-white shadow-lg transition-colors hover:bg-black/90`}>
          <Gamepad2 size={13} className={v.ok ? 'text-[#30D158]' : 'text-[#FF9F0A]'} />
          <span className="text-[10px] font-black" data-testid="remote-ctl-status-mini">{v.ok ? 'ON' : '학습'}</span>
          <ChevronLeft size={12} className="text-white/70" />
        </button>
      ) : (
      <div className={`fixed top-16 left-1/2 -translate-x-1/2 ${blocked ? 'z-[40]' : 'z-[70]'} flex items-center gap-1.5 whitespace-nowrap rounded-full bg-black/75 backdrop-blur-md px-2 py-1.5 text-white shadow-lg`}
           data-testid="remote-ctl-bar">
        <Gamepad2 size={14} className="text-[#30D158] ml-1" />
        <span className="text-[11px] font-black whitespace-nowrap" data-testid="remote-ctl-status">{v.ok ? '리모컨 ON' : '학습 필요'}</span>
        <Chip testId="remote-ctl-learn-start" onClick={() => { setDraft({}); setLearn(0); }}>버튼 학습</Chip>
        {onShutter && v.ok && <Chip testId="remote-ctl-learn-shutter" onClick={() => setLearn('shutter')}>{shutter ? '셔터 ✓' : '셔터 학습'}</Chip>}
        <Chip testId="remote-ctl-exit" tone="red" onClick={onExit}>끄기</Chip>
        <Chip testId="remote-ctl-collapse" onClick={() => toggleCollapsed(true)}>접기</Chip>
      </div>
      )}
      {!v.ok && map && <p className="fixed top-[6.5rem] inset-x-4 z-[70] text-center text-[11px] text-[#FF9F0A] font-black" data-testid="remote-ctl-map-error">{v.reason}</p>}
      {learn != null && <LearnPrompt step={learn} onCancel={() => { setLearn(null); setDraft({}); }} />}
      {menu && learn == null && <RemoteMenuPanel menu={menu} ctx={ctx} library={library} past={past} />}
      {feedback && (
        <div className={`fixed top-[6.5rem] left-1/2 -translate-x-1/2 z-[72] whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-black shadow-lg animate-in fade-in duration-150 ${feedback.ok ? 'bg-[#30D158] text-black' : 'bg-[#2C2C2E] text-white'}`}
             data-testid="remote-ctl-feedback">{feedback.text}</div>
      )}
    </>
  );
};

export default RemoteControlLayer;
